import React from 'react';
import { WeeklyHours, TemporaryClosure } from '../services/storeHoursApiService.ts';
import './StoreChangesConfirmation.css';

interface StoreChangesConfirmationProps {
  storeName: string;
  oldHours?: WeeklyHours;
  newHours?: WeeklyHours;
  oldClosures?: TemporaryClosure[];
  newClosures?: TemporaryClosure[];
  onConfirm: () => void;
  onCancel: () => void;
}

const formatTimeToDisplay = (time: string): string => {
  const hourNum = parseInt(time.split(':')[0]);
  if (hourNum === 0) return '12am';
  if (hourNum < 12) return `${hourNum}am`;
  if (hourNum === 12) return '12pm';
  return `${hourNum - 12}pm`;
};

const formatDayHours = (dayHours: { open: string; close: string; closed: boolean }): string => {
  if (dayHours.closed) return 'Closed';
  return `${formatTimeToDisplay(dayHours.open)} — ${formatTimeToDisplay(dayHours.close)}`;
};

const StoreChangesConfirmation: React.FC<StoreChangesConfirmationProps> = ({
  storeName,
  oldHours,
  newHours,
  oldClosures = [],
  newClosures = [],
  onConfirm,
  onCancel
}) => {
  const hoursChanged = oldHours && newHours && JSON.stringify(oldHours) !== JSON.stringify(newHours);
  const closuresChanged = JSON.stringify(oldClosures) !== JSON.stringify(newClosures);
  
  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ] as const;

  // Find changed hours
  const changedHours = hoursChanged && oldHours && newHours ? days.filter(day => {
    const oldDay = oldHours[day.key];
    const newDay = newHours[day.key];
    return JSON.stringify(oldDay) !== JSON.stringify(newDay);
  }) : [];

  // Analyze closure changes
  const addedClosures = newClosures.filter(newClosure => 
    !oldClosures.some(oldClosure => 
      oldClosure.date.getTime() === newClosure.date.getTime() && 
      oldClosure.reason === newClosure.reason
    )
  );

  const removedClosures = oldClosures.filter(oldClosure =>
    !newClosures.some(newClosure =>
      newClosure.date.getTime() === oldClosure.date.getTime() &&
      newClosure.reason === oldClosure.reason
    )
  );

  const modifiedClosures = newClosures.filter(newClosure => {
    const matchingOld = oldClosures.find(oldClosure =>
      oldClosure.date.getTime() === newClosure.date.getTime() &&
      oldClosure.reason === newClosure.reason
    );
    
    return matchingOld && (
      matchingOld.approved !== newClosure.approved ||
      matchingOld.reason !== newClosure.reason
    );
  });

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="store-changes-confirmation-overlay">
      <div className="store-changes-confirmation-modal">
        <div className="confirmation-header">
          <h2>Confirm Changes</h2>
          <p>Review your changes to <strong>{storeName}</strong> before saving:</p>
        </div>

        <div className="changes-content">
          {hoursChanged && changedHours.length > 0 && (
            <div className="hours-changes-section">
              <h3>📅 Regular Hours Changes</h3>
              <div className="hours-changes-list">
                {changedHours.map(day => {
                  const oldDay = oldHours![day.key];
                  const newDay = newHours![day.key];
                  return (
                    <div key={day.key} className="hours-change-item">
                      <div className="day-name">{day.label}:</div>
                      <div className="hours-comparison">
                        <span className="old-hours">{formatDayHours(oldDay)}</span>
                        <span className="arrow">→</span>
                        <span className="new-hours">{formatDayHours(newDay)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {closuresChanged && (
            <div className="closures-changes-section">
              <h3>🚫 Temporary Closures Changes</h3>
              
              {addedClosures.length > 0 && (
                <div className="closures-subsection">
                  <h4>Added Closures:</h4>
                  {addedClosures.map((closure, index) => (
                    <div key={index} className="closure-change-item added">
                      <span className="closure-date">{formatDate(closure.date)}</span>
                      <span className="closure-reason">{closure.reason}</span>
                      <span className={`closure-status ${closure.approved ? 'approved' : 'pending'}`}>
                        {closure.approved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {modifiedClosures.length > 0 && (
                <div className="closures-subsection">
                  <h4>Modified Closures:</h4>
                  {modifiedClosures.map((closure, index) => {
                    const oldClosure = oldClosures.find(old =>
                      old.date.getTime() === closure.date.getTime() &&
                      old.reason === closure.reason
                    );
                    return (
                      <div key={index} className="closure-change-item modified">
                        <span className="closure-date">{formatDate(closure.date)}</span>
                        <span className="closure-reason">{closure.reason}</span>
                        <div className="approval-change">
                          <span className={`old-status ${oldClosure?.approved ? 'approved' : 'pending'}`}>
                            {oldClosure?.approved ? 'Approved' : 'Pending'}
                          </span>
                          <span className="arrow">→</span>
                          <span className={`new-status ${closure.approved ? 'approved' : 'pending'}`}>
                            {closure.approved ? 'Approved' : 'Pending'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {removedClosures.length > 0 && (
                <div className="closures-subsection">
                  <h4>Removed Closures:</h4>
                  {removedClosures.map((closure, index) => (
                    <div key={index} className="closure-change-item removed">
                      <span className="closure-date">{formatDate(closure.date)}</span>
                      <span className="closure-reason">{closure.reason}</span>
                      <span className={`closure-status ${closure.approved ? 'approved' : 'pending'}`}>
                        {closure.approved ? 'Was Approved' : 'Was Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!hoursChanged && !closuresChanged && (
            <div className="no-changes">
              <p>No changes detected.</p>
            </div>
          )}
        </div>

        <div className="confirmation-actions">
          <button onClick={onCancel} className="cancel-button">
            Cancel
          </button>
          <button onClick={onConfirm} className="confirm-button">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoreChangesConfirmation;